import React from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Lock, ChevronLeft, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LookupField } from '@/components/shared/lookup-field';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/use-auth';
import { toDateInputValue, parseNzWallToUtc } from '@/lib/format-date';

interface PersonRef {
  _id: string;
  fullName?: string | null;
  email?: string | null;
  /** Populated department — surfaced next to the name in TaskAssignment
   *  to mirror the Zoho task layout. */
  department?: { _id: string; name?: string | null } | null;
}

interface Task {
  _id: string;
  pointName: string | null;
  pointDescription: string | null;
  stage: string | null;
  fieldType: 'checkbox' | 'text' | 'number' | 'date' | 'select' | 'ref';
  fieldOptions: string[];
  refEndpoint: string | null;
  refLabel: string | null;
  required: boolean;
  assetField: string | null;
  responsiblePerson: PersonRef | null;
  otherStaff: PersonRef[];
}
interface Stage { name: string; tasks: Task[] }
interface WizardState {
  assetTransfer: any;
  isFallback: boolean;
  settingId?: string;
  stages: Stage[];
  answers: Record<string, any>;
  currentStageIndex: number;
}

// Built-in Stage 1: the "current → new" assignment refs that drive the
// transfer. Saved straight to AssetTransfer via PUT.
interface BuiltinRef {
  name: keyof BuiltinShape;
  label: string;
  endpoint: string;
  refLabel?: string;
  required?: boolean;
}

// Asset is the only "header" ref — it identifies the row being transferred
// and renders full-width above the Current / New comparison grid.
const ASSET_REF: BuiltinRef = {
  name: 'asset', label: 'Asset',
  endpoint: '/fleet/assets', refLabel: 'fleetNumber', required: true,
};

// Current ↔ New pairs render side-by-side so the user reads each row as
// "moving from X to Y". Grouping is by the field type (Company, Site, etc.)
// — current on the left, new on the right.
interface BuiltinRefPair { label: string; current: BuiltinRef; next: BuiltinRef }

const REF_PAIRS: BuiltinRefPair[] = [
  {
    label: 'Company',
    current: { name: 'currentCompany', label: 'Current Company', endpoint: '/master-data/companies' },
    next:    { name: 'newCompany',     label: 'New Company',     endpoint: '/master-data/companies' },
  },
  {
    label: 'Site',
    current: { name: 'currentSite', label: 'Current Site', endpoint: '/master-data/sites' },
    next:    { name: 'newSite',     label: 'New Site',     endpoint: '/master-data/sites' },
  },
  {
    label: 'Department',
    current: { name: 'currentDepartment', label: 'Current Department', endpoint: '/master-data/departments' },
    next:    { name: 'newDepartment',     label: 'New Department',     endpoint: '/master-data/departments' },
  },
  {
    label: 'Location',
    current: { name: 'currentLocation', label: 'Current Location', endpoint: '/master-data/service-locations' },
    next:    { name: 'newLocation',     label: 'New Location',     endpoint: '/master-data/service-locations' },
  },
  {
    label: 'Vehicle Group',
    current: { name: 'currentVehicleGroup', label: 'Current Vehicle Group', endpoint: '/master-data/vehicle-groups', refLabel: 'group' },
    next:    { name: 'newVehicleGroup',     label: 'New Vehicle Group',     endpoint: '/master-data/vehicle-groups', refLabel: 'group' },
  },
  {
    label: 'Service Plan',
    current: { name: 'currentServicePlan', label: 'Current Service Plan', endpoint: '/servicing/plans' },
    next:    { name: 'newServicePlan',     label: 'New Service Plan',     endpoint: '/servicing/plans' },
  },
];

// Flat view of every BUILTIN ref — used by the save/load mapping code below
// (saveBuiltin, builtinStageOk, etc.) which still walks every field. Order
// here doesn't drive the visual layout — that lives in REF_PAIRS.
const BUILTIN_REFS: BuiltinRef[] = [
  ASSET_REF,
  ...REF_PAIRS.flatMap((p) => [p.current, p.next]),
];

interface BuiltinShape {
  asset: string | null;
  currentCompany: string | null;
  currentSite: string | null;
  currentDepartment: string | null;
  currentLocation: string | null;
  currentVehicleGroup: string | null;
  currentServicePlan: string | null;
  newCompany: string | null;
  newSite: string | null;
  newDepartment: string | null;
  newLocation: string | null;
  newVehicleGroup: string | null;
  newServicePlan: string | null;
}

// ---------- Stepper (same as onboard) ----------

type StepStatus = 'done' | 'current' | 'locked';
function Stepper({
  steps, currentIndex, maxReachedIndex, onJump,
}: {
  steps: string[];
  currentIndex: number;
  maxReachedIndex: number;
  onJump: (i: number) => void;
}) {
  return (
    <ol className="flex flex-wrap items-center gap-1 py-3 px-4 border-b bg-muted/30">
      {steps.map((label, i) => {
        let status: StepStatus = i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'locked';
        const canJump = i <= maxReachedIndex;
        return (
          <li key={label + i} className="flex items-center">
            <button type="button" onClick={() => canJump && onJump(i)} disabled={!canJump}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors',
                status === 'current' && 'bg-primary text-primary-foreground font-medium',
                status === 'done' && 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200',
                status === 'locked' && 'text-muted-foreground bg-muted/40 cursor-not-allowed',
              )}>
              <span className={cn(
                'inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px]',
                status === 'current' && 'bg-primary-foreground text-primary',
                status === 'done' && 'bg-emerald-200 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-100',
                status === 'locked' && 'bg-muted text-muted-foreground',
              )}>
                {status === 'done' ? <Check className="h-3 w-3" /> : status === 'locked' ? <Lock className="h-3 w-3" /> : i + 1}
              </span>
              <span className="truncate max-w-[160px]">{label}</span>
            </button>
            {i < steps.length - 1 && <span className="mx-1 text-muted-foreground">›</span>}
          </li>
        );
      })}
    </ol>
  );
}

// ---------- Per-task field renderer (same as onboard) ----------

/** Footer strip under each task showing who owns it. Renders inline so the
 *  user can see the responsible person + any extra staff alongside the
 *  task input. Hidden entirely when neither field is set on the config. */
function TaskAssignment({ task }: { task: Task }) {
  const personLabel = (p: PersonRef): string => p.fullName?.trim() || p.email || '—';
  const departmentLabel = (p: PersonRef): string | null => p.department?.name?.trim() || null;
  const renderPerson = (p: PersonRef) => {
    const dept = departmentLabel(p);
    return (
      <span className="font-medium">
        {personLabel(p)}
        {dept && <span className="text-muted-foreground font-normal"> ({dept})</span>}
      </span>
    );
  };
  const hasResponsible = !!task.responsiblePerson;
  const hasOthers = (task.otherStaff?.length ?? 0) > 0;
  if (!hasResponsible && !hasOthers) return null;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs pt-1 border-t mt-2">
      {hasResponsible && (
        <div>
          <span className="text-muted-foreground">Responsible: </span>
          {renderPerson(task.responsiblePerson!)}
        </div>
      )}
      {hasOthers && (
        <div>
          <span className="text-muted-foreground">Other staff: </span>
          {task.otherStaff.map((p, i) => (
            <React.Fragment key={p._id}>
              {i > 0 && <span>, </span>}
              {renderPerson(p)}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskField({ task, value, onChange, disabled }: {
  task: Task; value: any; onChange: (v: any) => void; disabled?: boolean;
}) {
  switch (task.fieldType) {
    case 'checkbox':
      return (
        <label className="inline-flex items-center gap-2 text-sm">
          <Checkbox checked={!!value} disabled={disabled} onCheckedChange={(v) => onChange(v === true)} />
          <span>{task.pointName || 'Confirmed'}</span>
        </label>
      );
    case 'text':   return <Input value={value ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
    case 'number': return <Input type="number" value={value ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />;
    case 'date':   return <Input type="date" value={value ? toDateInputValue(value) : ''} disabled={disabled} onChange={(e) => onChange(e.target.value ? parseNzWallToUtc(`${e.target.value}T00:00`)?.toISOString() ?? null : null)} />;
    case 'select':
      return (
        <Select value={value ?? '__none__'} onValueChange={(v) => onChange(v === '__none__' ? null : v)} disabled={disabled}>
          <SelectTrigger><SelectValue placeholder="— Select —" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— None —</SelectItem>
            {(task.fieldOptions ?? []).map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    case 'ref': {
      if (!task.refEndpoint) return <Input value="" disabled />;
      const labelKey = task.refLabel || 'name';
      const id = typeof value === 'object' && value ? (value.id ?? value._id) : value;
      return (
        <LookupField
          endpoint={task.refEndpoint}
          labelKey={labelKey}
          value={id ?? null}
          onChange={(picked) => onChange(picked ?? null)}
          disabled={disabled}
          placeholder="— Select —"
        />
      );
    }
  }
}

// Built-in ref dropdown — fetches options once and renders a Radix Select.
function BuiltinRefField({ endpoint, refLabel, value, onChange, disabled }: {
  endpoint: string; refLabel: string; value: string | null; onChange: (v: string | null) => void; disabled?: boolean;
}) {
  // Delegate to the shared lookup widget so the wizard's pickers behave
  // identically to the main forms — Popover + type-to-search + truncation
  // hint at 200.
  return (
    <LookupField
      endpoint={endpoint}
      labelKey={refLabel}
      value={value}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

// Read-only label display for the "Current X" snapshot fields — resolves the
// ref id to its label using the same endpoint as the picker. Renders as a
// muted text panel so the user can see the value isn't editable.
function BuiltinRefDisplay({ endpoint, refLabel, value }: {
  endpoint: string; refLabel: string; value: string | null;
}) {
  const { data: opts = [] } = useQuery<any[]>({
    queryKey: ['builtin-ref', endpoint],
    queryFn: async () => (await api<any[]>(`${endpoint}?limit=500`)).data || [],
    enabled: !!value,
  });
  const label = value ? (opts.find((o) => o.id === value)?.[refLabel] ?? value) : null;
  return (
    <div className="h-9 px-3 rounded-md border bg-muted/40 text-sm flex items-center text-muted-foreground">
      {label ?? '—'}
    </div>
  );
}

// ---------- Wizard page ----------

/** Field-level access rule:
 *  - If a task names a responsiblePerson or any otherStaff, only those people
 *    can edit the corresponding field.
 *  - If a task names no-one, anyone with wizard access can edit (legacy
 *    behaviour — admins haven't pinned ownership yet).
 *  Returns `true` when the user is allowed to edit. */
function canEditTask(task: Task | null | undefined, userId: string | null | undefined): boolean {
  if (!task) return true;
  const responsibleId = task.responsiblePerson?._id ?? null;
  const otherIds = (task.otherStaff ?? []).map((p) => p._id);
  const hasAssignees = !!responsibleId || otherIds.length > 0;
  if (!hasAssignees) return true;
  if (!userId) return false;
  return responsibleId === userId || otherIds.includes(userId);
}

export function AssetTransferWizardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { employee } = useAuth();
  const currentUserId = employee?.id ?? null;

  const { data, isLoading, error } = useQuery<{ data: WizardState }>({
    queryKey: ['/asset-transfers/wizard-state', id],
    queryFn: async () => api<WizardState>(`/asset-transfers/${id}/wizard-state`) as any,
    enabled: !!id,
  });
  const state = data?.data;

  const builtinStageName = 'Assignment';
  const finalizeStageName = 'Finalize';

  // The "Asset Reassignment" config stage is metadata-only: each task
  // mirrors a built-in side-by-side reassignment field and exists solely
  // to carry a responsiblePerson/otherStaff assignment. We don't render
  // it as a wizard step — instead we attach those people inline beneath
  // each "New X" picker. Index translation against `state.stages` (which
  // still contains the synthetic stage from the API) is handled below.
  const RESERVED_STAGE_NAME = 'Asset Reassignment';

  // Lookup keyed by the BUILTIN_REFS field name (e.g. "newCompany").
  // Populated from synthetic-stage tasks whose `sourceField` matches.
  const assignmentTaskBySource = React.useMemo<Map<string, Task>>(() => {
    const m = new Map<string, Task>();
    if (!state) return m;
    for (const s of state.stages) {
      if (s.name !== RESERVED_STAGE_NAME) continue;
      for (const t of s.tasks as any[]) {
        const key = t.sourceField as string | null | undefined;
        if (key) m.set(key, t);
      }
    }
    return m;
  }, [state]);

  // Stages the wizard actually renders as steps (synthetic stage stripped).
  const visibleStages = React.useMemo(() => {
    if (!state) return [] as Stage[];
    return state.stages.filter((s) => s.name !== RESERVED_STAGE_NAME);
  }, [state]);

  // Map server-side `currentStageIndex` (over all stages, incl. synthetic)
  // to the visible-stages index space used by stepper math. The synthetic
  // stage has only non-required tasks, so the server's progression logic
  // never lands ON it — it's always either before or after.
  const visibleCurrentStageIndex = React.useMemo(() => {
    if (!state) return -1;
    const ci = state.currentStageIndex;
    if (ci < 0) return -1;
    if (ci >= state.stages.length) return visibleStages.length;
    let count = 0;
    for (let i = 0; i < ci; i++) {
      if (state.stages[i].name !== RESERVED_STAGE_NAME) count++;
    }
    return count;
  }, [state, visibleStages.length]);

  const allStages = React.useMemo<string[]>(() => {
    if (!state) return [builtinStageName, finalizeStageName];
    if (state.isFallback) return [builtinStageName, finalizeStageName];
    return [builtinStageName, ...visibleStages.map((s) => s.name), finalizeStageName];
  }, [state, visibleStages]);

  const [builtin, setBuiltin] = React.useState<BuiltinShape | null>(null);
  const [draftAnswers, setDraftAnswers] = React.useState<Record<string, any>>({});
  const [stepIndex, setStepIndex] = React.useState(0);
  const [maxReachedIndex, setMaxReachedIndex] = React.useState(0);
  // Tracks the asset id we've already pulled "current X" snapshot data from,
  // so we only re-sync when the user changes the asset selection.
  const lastSyncedAssetIdRef = React.useRef<string | null>(null);
  // Seed local state from the wizard-state response only on first arrival.
  // After Continue triggers a save + queryInvalidate, the refetched payload
  // still reports the same `currentStageIndex` (no new task answers yet),
  // which used to snap `stepIndex` back to 0 and make Continue look broken.
  // Matches the fix already applied to onboard-wizard.tsx.
  const initRef = React.useRef<string | null>(null);

  // Pull the full asset record (with populated refs) whenever the user has
  // selected an asset. The `current*` snapshot fields auto-populate from
  // these, so the user can't drift the snapshot away from reality.
  const { data: assetDoc } = useQuery<any>({
    queryKey: ['/fleet/assets', builtin?.asset],
    queryFn: async () => (await api<any>(`/fleet/assets/${builtin!.asset}`)).data || null,
    enabled: !!builtin?.asset,
  });

  React.useEffect(() => {
    if (!builtin?.asset || !assetDoc) return;
    if (lastSyncedAssetIdRef.current === builtin.asset) return;
    const refToId = (v: any): string | null =>
      v == null ? null : (typeof v === 'object' ? (v.id ?? v._id ?? null) : v);
    const snapshot: Partial<BuiltinShape> = {
      // Persist the user's asset selection alongside the snapshot. Without
      // this the save only contained the `current*` fields, so after the
      // wizard-state refetch the server still reported `asset: null` and
      // the user's pick was effectively unsaved — also showed up as
      // "fields wiped" when the old [state] effect re-seeded local state.
      asset:               builtin.asset,
      currentCompany:      refToId(assetDoc.company),
      currentDepartment:   refToId(assetDoc.department),
      currentVehicleGroup: refToId(assetDoc.vehicleGroup),
      // Asset has no direct site / serviceLocation / servicePlan field. We
      // leave those null in the snapshot — the read-only display shows "—".
      currentSite:         null,
      currentLocation:     null,
      currentServicePlan:  null,
    };
    setBuiltin((p) => p ? { ...p, ...snapshot } : p);
    // Persist the snapshot to the transfer so it doesn't drift on refresh.
    saveBuiltin.mutate(snapshot);
    lastSyncedAssetIdRef.current = builtin.asset;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builtin?.asset, assetDoc]);

  React.useEffect(() => {
    if (!state) return;
    if (initRef.current === id) return;
    initRef.current = id ?? null;
    const x = state.assetTransfer;
    const refToId = (v: any): string | null => v == null ? null : (typeof v === 'object' ? (v.id ?? v._id ?? null) : v);
    setBuiltin({
      asset: refToId(x.asset),
      currentCompany: refToId(x.currentCompany),
      currentSite: refToId(x.currentSite),
      currentDepartment: refToId(x.currentDepartment),
      currentLocation: refToId(x.currentLocation),
      currentVehicleGroup: refToId(x.currentVehicleGroup),
      currentServicePlan: refToId(x.currentServicePlan),
      newCompany: refToId(x.newCompany),
      newSite: refToId(x.newSite),
      newDepartment: refToId(x.newDepartment),
      newLocation: refToId(x.newLocation),
      newVehicleGroup: refToId(x.newVehicleGroup),
      newServicePlan: refToId(x.newServicePlan),
    });
    setDraftAnswers({ ...state.answers });
    if (x.assetTransferred || x.locked || x.status === 'completed') {
      const last = (state.isFallback ? 1 : visibleStages.length + 1);
      setStepIndex(last);
      setMaxReachedIndex(last);
    } else {
      const start = state.isFallback ? 0 : visibleCurrentStageIndex + 1;
      setStepIndex(start);
      setMaxReachedIndex(Math.max(maxReachedIndex, start));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const saveBuiltin = useMutation({
    mutationFn: async (payload: Partial<BuiltinShape>) => {
      const r = await api(`/asset-transfers/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
      if (!r.success) throw new Error(r.error?.message || 'Save failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/asset-transfers/wizard-state', id] }),
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const saveAnswer = useMutation({
    mutationFn: async (p: { taskId: string; value: any }) => {
      const r = await api(`/asset-transfer-task-answers`, {
        method: 'POST',
        body: JSON.stringify({ assetTransfer: id, task: p.taskId, value: p.value }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Save failed');
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const finalize = useMutation({
    mutationFn: async () => {
      const r = await api(`/asset-transfers/${id}/finalize`, { method: 'POST' });
      if (!r.success) throw new Error(r.error?.message || 'Finalize failed');
      return r.data;
    },
    onSuccess: () => {
      toast('Transfer finalized', undefined, 'success');
      qc.invalidateQueries({ queryKey: ['/asset-transfers/wizard-state', id] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  if (isLoading || !state || !builtin) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (error) return <div className="p-8 text-destructive">Failed to load transfer: {String((error as any).message ?? error)}</div>;

  function builtinStageOk(): boolean {
    // Asset is structurally required — the wizard can't progress without it.
    if (!builtin!.asset) return false;
    // Each "New X" picker is only required when the admin marked the
    // corresponding synthetic-stage task as required in the Asset
    // Transferring Configuration.
    for (const pair of REF_PAIRS) {
      const t = assignmentTaskBySource.get(pair.next.name);
      if (t?.required && !builtin![pair.next.name]) return false;
    }
    return true;
  }
  function configStageOk(stageIdx: number): boolean {
    if (state!.isFallback) return true;
    const stage = visibleStages[stageIdx];
    if (!stage) return true;
    for (const t of stage.tasks) {
      if (!t.required) continue;
      const v = draftAnswers[t._id];
      const empty = v === undefined || v === null || v === '' || v === false;
      if (empty) return false;
    }
    return true;
  }

  const isBuiltinStep = stepIndex === 0;
  const isFinalizeStep = stepIndex === allStages.length - 1;
  const configStageIdx = stepIndex - 1;
  const currentStageEditable = stepIndex === maxReachedIndex;

  function canAdvance(): boolean {
    if (isBuiltinStep) return builtinStageOk();
    if (isFinalizeStep) return false;
    return configStageOk(configStageIdx);
  }

  async function handleContinue() {
    if (isBuiltinStep) {
      await saveBuiltin.mutateAsync(builtin!);
    } else if (!state!.isFallback && configStageIdx >= 0 && configStageIdx < visibleStages.length) {
      const stage = visibleStages[configStageIdx];
      for (const t of stage.tasks) {
        const v = draftAnswers[t._id];
        if (v !== undefined) {
          // eslint-disable-next-line no-await-in-loop
          await saveAnswer.mutateAsync({ taskId: t._id, value: v ?? null });
        }
      }
    }
    const next = stepIndex + 1;
    setStepIndex(next);
    setMaxReachedIndex(Math.max(maxReachedIndex, next));
  }
  function handleJump(i: number) { if (i <= maxReachedIndex) setStepIndex(i); }

  const x = state.assetTransfer;
  const finalized = x.assetTransferred || x.locked || x.status === 'completed';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/fleet-extras/asset-transfers"><ChevronLeft className="h-4 w-4" /> Back to transfers</Link>
        </Button>
        <div className="ml-auto text-xs text-muted-foreground">
          {state.isFallback ? (
            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-3.5 w-3.5" /> No active transfer configuration — showing fallback form.
            </span>
          ) : (
            <span>Configuration: <code className="text-foreground">{state.settingId?.slice(-6)}</code></span>
          )}
        </div>
      </div>

      <div>
        <h1 className="text-2xl font-semibold">
          {x.requestNumber ?? 'Transfer'} — {(x.asset && (typeof x.asset === 'object' ? x.asset.fleetNumber : '')) ?? ''}
        </h1>
        <p className="text-sm text-muted-foreground">
          Status: <span className="font-medium">{x.status ?? 'Draft'}</span>
          {finalized && <span className="ml-2 text-emerald-600 dark:text-emerald-400">✓ Finalized</span>}
        </p>
      </div>

      {state.isFallback && (
        // Prominent banner — the small header indicator alone wasn't enough
        // to warn an operator that they're working on a transfer whose
        // finalize endpoint will refuse to commit. Most likely cause: the
        // active transfer configuration was deactivated between this
        // transfer being created and being worked on.
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 p-3 flex items-start gap-2 text-sm">
          <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium text-amber-800 dark:text-amber-200">No active Asset Transferring Configuration</div>
            <div className="text-amber-700 dark:text-amber-300/90">
              The fallback form will save edits, but finalize is blocked until an admin activates a Transfer configuration in Settings.
            </div>
          </div>
        </div>
      )}

      <Card className="overflow-hidden">
        <Stepper steps={allStages} currentIndex={stepIndex} maxReachedIndex={maxReachedIndex} onJump={handleJump} />

        <CardContent className="p-6 space-y-4">
          {isBuiltinStep && (
            <div className="space-y-4">
              {/* Asset is the row identifier — render it full-width on top
                  so it doesn't compete with the Current/New pairs below. */}
              <div data-field={ASSET_REF.name}>
                <Label className="text-xs">
                  {ASSET_REF.label}{ASSET_REF.required && <span className="text-destructive"> *</span>}
                </Label>
                <BuiltinRefField
                  endpoint={ASSET_REF.endpoint}
                  refLabel={ASSET_REF.refLabel ?? 'name'}
                  value={builtin[ASSET_REF.name]}
                  onChange={(v) => setBuiltin((p) => ({ ...(p as BuiltinShape), [ASSET_REF.name]: v }))}
                />
              </div>

              {/* Header row labels — only on screens wide enough for the
                  two columns to be visually paired. Below md the columns
                  stack and the per-field labels carry the meaning. */}
              <div className="hidden md:grid grid-cols-2 gap-4 text-[11px] uppercase tracking-wide text-muted-foreground pt-2 border-t">
                <div>Current</div>
                <div>New</div>
              </div>

              {/* Current ↔ New side-by-side. Each row reads as "moving
                  from X to Y". On mobile (<md) they stack one above the
                  other but the per-field labels still distinguish them.
                  The "Current X" column is read-only — it snapshots the
                  asset's current state when the user picks the asset, so
                  the transfer record can't drift away from reality. */}
              <div className="space-y-3">
                {REF_PAIRS.map((pair) => {
                  // Surface the per-pair assignment (responsiblePerson +
                  // otherStaff) under the "New" column. The synthetic
                  // "Asset Reassignment" stage carries this metadata,
                  // keyed by the BUILTIN_REFS field name (e.g.
                  // 'newCompany'); see assignmentTaskBySource above.
                  const assignmentTask = assignmentTaskBySource.get(pair.next.name) || null;
                  const allowedToEdit = canEditTask(assignmentTask, currentUserId);
                  const isRequired = !!assignmentTask?.required;
                  return (
                    <div key={pair.label} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Current — read-only display, auto-filled from asset. */}
                      <div data-field={pair.current.name}>
                        <Label className="text-xs">{pair.current.label}</Label>
                        <BuiltinRefDisplay
                          endpoint={pair.current.endpoint}
                          refLabel={pair.current.refLabel ?? 'name'}
                          value={builtin[pair.current.name]}
                        />
                      </div>
                      {/* New — editable picker for the responsible person /
                          other staff named on the assignment task. Other
                          users see the field disabled with a tooltip. */}
                      <div data-field={pair.next.name}>
                        <Label className="text-xs">
                          {pair.next.label}{isRequired && <span className="text-destructive"> *</span>}
                        </Label>
                        <div title={allowedToEdit ? undefined : 'Only the responsible person or other staff named on this task can change this field.'}>
                          <BuiltinRefField
                            endpoint={pair.next.endpoint}
                            refLabel={pair.next.refLabel ?? 'name'}
                            value={builtin[pair.next.name]}
                            onChange={(v) => setBuiltin((p) => ({ ...(p as BuiltinShape), [pair.next.name]: v }))}
                            disabled={!allowedToEdit}
                          />
                        </div>
                        {assignmentTask && <TaskAssignment task={assignmentTask} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!isBuiltinStep && !isFinalizeStep && !state.isFallback && (
            <div className="space-y-4">
              <h2 className="text-lg font-medium">{visibleStages[configStageIdx]?.name}</h2>
              {visibleStages[configStageIdx]?.tasks.length === 0 && (
                <p className="text-sm text-muted-foreground">No tasks configured in this stage.</p>
              )}
              <div className="space-y-3">
                {visibleStages[configStageIdx]?.tasks.map((t) => {
                  const allowedToEdit = canEditTask(t, currentUserId);
                  return (
                    <Card key={t._id} className="border-border/60">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          {t.pointName ?? '(unnamed task)'}
                          {t.required && <span className="text-destructive text-xs">*</span>}
                        </CardTitle>
                        {t.pointDescription && <p className="text-xs text-muted-foreground">{t.pointDescription}</p>}
                      </CardHeader>
                      <CardContent className="pt-0 space-y-2">
                        <div title={allowedToEdit ? undefined : 'Only the responsible person or other staff named on this task can change this field.'}>
                          <TaskField
                            task={t}
                            value={draftAnswers[t._id]}
                            onChange={(v) => setDraftAnswers((p) => ({ ...p, [t._id]: v }))}
                            disabled={!currentStageEditable || !allowedToEdit}
                          />
                        </div>
                        <TaskAssignment task={t} />
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {isFinalizeStep && (
            <div className="space-y-3">
              <h2 className="text-lg font-medium">Finalize transfer</h2>
              <p className="text-sm text-muted-foreground">
                {finalized
                  ? 'This transfer is already finalized. Use the back button to leave.'
                  : 'The asset record will be updated with the new assignments and the transfer locked.'}
              </p>
              <Button disabled={finalized || finalize.isPending} onClick={() => finalize.mutate()}>
                {finalize.isPending ? 'Finalizing…' : finalized ? 'Already finalized' : 'Finalize transfer'}
              </Button>
            </div>
          )}
        </CardContent>

        <div className="flex items-center justify-between border-t bg-muted/30 px-6 py-3">
          <Button variant="outline" disabled={stepIndex === 0} onClick={() => setStepIndex(Math.max(0, stepIndex - 1))}>← Back</Button>
          {!isFinalizeStep && (
            <Button onClick={handleContinue} disabled={!canAdvance() || saveBuiltin.isPending || saveAnswer.isPending}>
              {saveBuiltin.isPending || saveAnswer.isPending ? 'Saving…' : 'Continue →'}
            </Button>
          )}
          {isFinalizeStep && (
            <Button variant="outline" onClick={() => navigate('/fleet-extras/asset-transfers')}>Done</Button>
          )}
        </div>
      </Card>
    </div>
  );
}
